import { motion } from 'framer-motion';
import { X, Loader2, Camera, Sparkles } from 'lucide-react';
import { useLang } from '@/contexts/LangContext';

export default function CompleteMissionModal({
  open,
  prompts,
  promptsLoading,
  answers,
  setAnswers,
  notes,
  setNotes,
  proofPath,
  onUpload,
  completing,
  onClose,
  onSubmit,
}) {
  const { t } = useLang();
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white rounded-3xl w-full max-w-md p-6 border-b-4 border-[#E5E5E5]"
        onClick={(e) => e.stopPropagation()}
        data-testid="complete-modal"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-extrabold">{t('mark_complete')}</h3>
          <button onClick={onClose} className="p-1" data-testid="close-complete-modal">
            <X size={24} strokeWidth={3} />
          </button>
        </div>

        <div className="max-h-[65vh] overflow-y-auto -mx-6 px-6">
          {promptsLoading ? (
            <div className="flex items-center justify-center py-6 gap-2 text-[#AFAFAF]">
              <Loader2 size={20} className="animate-spin text-[#1CB0F6]" strokeWidth={3} />
              <span className="text-sm font-extrabold">Your coach is preparing a few quick questions…</span>
            </div>
          ) : prompts.length > 0 ? (
            <div className="space-y-4 mb-4" data-testid="complete-prompts">
              <p className="text-xs uppercase font-extrabold text-[#FFC800] flex items-center gap-1">
                <Sparkles size={12} strokeWidth={3} /> Quick check-in
              </p>
              {prompts.map((p, qi) => (
                <div key={qi} data-testid={`complete-prompt-${qi}`}>
                  <p className="text-sm font-extrabold leading-snug">{p.question}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {p.options.map((opt) => {
                      const selected = answers[p.question] === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setAnswers((a) => ({ ...a, [p.question]: opt }))}
                          className={`px-3 py-1.5 rounded-full text-xs font-extrabold border-2 border-b-4 transition-colors ${
                            selected
                              ? 'bg-[#1CB0F6] text-white border-[#1899D6]'
                              : 'bg-white border-[#E5E5E5] text-[#4B4B4B]'
                          }`}
                          data-testid={`complete-prompt-${qi}-opt-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <label className="block">
            <span className="text-xs uppercase font-extrabold text-[#AFAFAF]">{t('add_note')}</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="gm-input mt-2 text-base"
              rows={2}
              placeholder="Anything else?"
              data-testid="complete-notes-input"
            />
          </label>

          <label className="block mt-3">
            <span className="text-xs uppercase font-extrabold text-[#AFAFAF]">{t('upload_proof')}</span>
            <div className="mt-2 flex gap-2">
              <label className="gm-btn gm-btn-secondary flex-1 py-3 cursor-pointer" data-testid="upload-proof-button">
                <Camera size={20} strokeWidth={3} />
                <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
                {proofPath ? 'Uploaded ✓' : 'Choose Photo'}
              </label>
            </div>
          </label>
        </div>

        <button
          onClick={onSubmit}
          disabled={completing}
          className="gm-btn gm-btn-success w-full text-xl py-4 mt-5"
          data-testid="confirm-complete-button"
        >
          {completing ? '…' : t('confirm')}
        </button>
      </motion.div>
    </div>
  );
}
